import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from '@/core/hooks/useAuth';
import { getLoginAD } from '@/core/configs/auth/auth.utils';
import {
    getAvatarUrl,
    notifyAvatarUpdate,
    subscribeAvatarUpdates,
} from '@/services/avatar.service';
import { carregarEstadoLgpd, subscribeLgpd } from '@/services/lgpd.service';
import { subscribePreferencias, syncPreferenciasFromApi } from '@/services/preferencias.service';

interface AvatarContextType {
    avatarUrl: string | null;
    setAvatarUrl: (url: string | null) => void;
}

const AvatarContext = createContext<AvatarContextType | undefined>(undefined);

export const AvatarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { usuarioSessao } = useAuth();
    const [avatarUrl, setAvatarUrlState] = useState<string | null>(null);

    const setAvatarUrl = useCallback((url: string | null) => {
        setAvatarUrlState(url);
    }, []);

    useEffect(() => {
        const unsubscribe = subscribeAvatarUpdates((url) => {
            setAvatarUrlState(url);
        });
        return unsubscribe;
    }, []);

    useEffect(() => {
        void carregarEstadoLgpd();
        const unsubscribeLgpd = subscribeLgpd(() => undefined);
        const unsubscribePreferencias = subscribePreferencias(() => undefined);
        return () => {
            unsubscribeLgpd();
            unsubscribePreferencias();
        };
    }, []);

    useEffect(() => {
        const loginAD = getLoginAD(usuarioSessao);
        if (!loginAD) {
            setAvatarUrlState(null);
            void syncPreferenciasFromApi(null);
            return;
        }

        void syncPreferenciasFromApi(loginAD);
        void getAvatarUrl(loginAD).then((url) => {
            setAvatarUrlState(url);
        });
    }, [usuarioSessao]);

    return (
        <AvatarContext.Provider value={{ avatarUrl, setAvatarUrl }}>
            {children}
        </AvatarContext.Provider>
    );
};

export const useAvatar = () => {
    const context = useContext(AvatarContext);
    if (!context) {
        throw new Error('useAvatar must be used within AvatarProvider');
    }
    return context;
};

export { notifyAvatarUpdate };
